<template>
    <Head title="Checklists" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <div>
                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Checklists</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Manage and conduct inspection checklists</p>
            </div>
            <div class="flex items-center space-x-3">
                <Link
                    :href="route('checklists.create')"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600 transition"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Create Checklist
                </Link>
            </div>
        </div>

        <!-- Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-4 border border-gray-200 dark:border-gray-700">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Search -->
                <input
                    v-model="localFilters.search"
                    type="text"
                    placeholder="Search checklists..."
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @input="debouncedSearch"
                />

                <!-- Status Filter -->
                <select
                    v-model="localFilters.status"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Status</option>
                    <option value="assigned">Assigned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="pending_review">Pending Review</option>
                    <option value="under_review">Under Review</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="rejected">Rejected</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                </select>

                <!-- Template Filter -->
                <select
                    v-model="localFilters.template_id"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Templates</option>
                    <option v-for="template in templates" :key="template.id" :value="template.id">
                        {{ template.title }}
                    </option>
                </select>

                <!-- Location Filter -->
                <select
                    v-model="localFilters.location_id"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Locations</option>
                    <option v-for="location in locations" :key="location.id" :value="location.id">
                        {{ location.name }}
                    </option>
                </select>
            </div>

            <!-- Additional Filters -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <!-- Date Range -->
                <div class="flex space-x-2">
                    <input
                        v-model="localFilters.date_from"
                        type="date"
                        class="flex-1 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="From"
                        @change="applyFilters"
                    />
                    <input
                        v-model="localFilters.date_to"
                        type="date"
                        class="flex-1 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="To"
                        @change="applyFilters"
                    />
                </div>

                <!-- SLA Breached -->
                <label class="flex items-center">
                    <input
                        v-model="localFilters.sla_breached"
                        type="checkbox"
                        class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        @change="applyFilters"
                    />
                    <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">SLA Breached Only</span>
                </label>

                <!-- Overdue -->
                <label class="flex items-center">
                    <input
                        v-model="localFilters.overdue"
                        type="checkbox"
                        class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        @change="applyFilters"
                    />
                    <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Overdue Only</span>
                </label>
            </div>

            <!-- Filter Actions -->
            <div class="flex justify-between items-center mt-4">
                <button
                    @click="clearFilters"
                    class="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                    Clear Filters
                </button>
            </div>
        </div>

        <!-- Checklists Table -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-700">
                    <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Checklist
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Template & Location
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Progress
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Assigned To
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Due Date
                        </th>
                        <th scope="col" class="relative px-6 py-3">
                            <span class="sr-only">Actions</span>
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr v-if="checklists.data.length === 0">
                        <td colspan="7" class="px-6 py-12 text-center">
                            <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No checklists found</h3>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new checklist.</p>
                        </td>
                    </tr>
                    <tr v-for="checklist in checklists.data" :key="checklist.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div>
                                <div class="text-sm font-medium text-gray-900 dark:text-white">
                                    {{ checklist.checklist_number }}
                                </div>
                                <div class="text-xs text-gray-500 dark:text-gray-400">
                                    Created {{ formatDate(checklist.created_at) }}
                                </div>
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-900 dark:text-white">{{ checklist.template?.title }}</div>
                            <div class="text-xs text-gray-500 dark:text-gray-400">{{ checklist.location?.name || 'No location' }}</div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-500 dark:text-gray-400">
                                <div v-if="checklist.responses_count !== undefined">
                                    {{ checklist.responses_count || 0 }} / {{ checklist.total_fields || 0 }} fields
                                </div>
                                <div v-else class="text-xs text-gray-400">
                                    Not started
                                </div>
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="flex flex-col">
                                <span :class="getStatusClass(checklist.status)">
                                    {{ getStatusLabel(checklist.status) }}
                                </span>
                                <span v-if="checklist.sla_breached" class="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                    SLA Breach
                                </span>
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            <div v-if="checklist.assigned_to">
                                {{ checklist.assigned_to.first_name }} {{ checklist.assigned_to.last_name }}
                            </div>
                            <div v-else class="text-xs text-gray-400 dark:text-gray-500">
                                Unassigned
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            <div v-if="checklist.due_date" :class="{ 'text-red-600 dark:text-red-400 font-medium': isOverdue(checklist.due_date, checklist.status) }">
                                {{ formatDate(checklist.due_date) }}
                            </div>
                            <div v-else>No due date</div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div class="flex items-center justify-end space-x-2">
                                <!-- Start/Continue Button -->
                                <Link
                                    v-if="checklist.status === 'assigned' || checklist.status === 'in_progress'"
                                    :href="route('checklists.conduct', checklist.id)"
                                    class="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                                    :title="checklist.status === 'assigned' ? 'Start' : 'Continue'"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </Link>

                                <!-- View Button -->
                                <Link
                                    :href="route('checklists.show', checklist.id)"
                                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                    title="View"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                </Link>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Pagination -->
            <div v-if="checklists.data.length > 0" class="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{ checklists.from }}</span>
                            to
                            <span class="font-medium">{{ checklists.to }}</span>
                            of
                            <span class="font-medium">{{ checklists.total }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                            <component
                                v-for="(link, index) in checklists.links"
                                :key="index"
                                :is="link.url ? Link : 'span'"
                                :href="link.url"
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    link.active
                                        ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 text-blue-600 dark:text-blue-400'
                                        : link.url
                                            ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                            : 'bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-600 cursor-not-allowed',
                                    index === 0 ? 'rounded-l-md' : '',
                                    index === checklists.links.length - 1 ? 'rounded-r-md' : ''
                                ]"
                                v-html="link.label"
                            />
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';

const props = defineProps({
    checklists: Object,
    filters: Object,
    templates: Array,
    locations: Array,
});

const { formatDateTime: formatDateTimeInTZ } = useTimezone();

const localFilters = ref({
    search: props.filters.search || '',
    status: props.filters.status || '',
    template_id: props.filters.template_id || '',
    location_id: props.filters.location_id || '',
    date_from: props.filters.date_from || '',
    date_to: props.filters.date_to || '',
    sla_breached: props.filters.sla_breached || false,
    overdue: props.filters.overdue || false,
});

let debounceTimer = null;

const debouncedSearch = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        applyFilters();
    }, 500);
};

const applyFilters = () => {
    router.get(route('checklists.index'), localFilters.value, {
        preserveState: true,
        preserveScroll: true,
    });
};

const clearFilters = () => {
    localFilters.value = {
        search: '',
        status: '',
        template_id: '',
        location_id: '',
        date_from: '',
        date_to: '',
        sla_breached: false,
        overdue: false,
    };
    applyFilters();
};

const getStatusClass = (status) => {
    const classes = {
        assigned: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        in_progress: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        pending_review: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        under_review: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
        pending_approval: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        rejected: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        completed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        cancelled: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    };
    return classes[status] || classes.assigned;
};

const getStatusLabel = (status) => {
    const labels = {
        assigned: 'Assigned',
        in_progress: 'In Progress',
        pending_review: 'Pending Review',
        under_review: 'Under Review',
        pending_approval: 'Pending Approval',
        rejected: 'Rejected',
        completed: 'Completed',
        cancelled: 'Cancelled',
    };
    return labels[status] || status;
};

const isOverdue = (dueDate, status) => {
    if (!dueDate) return false;
    if (['completed', 'cancelled'].includes(status)) return false;
    return new Date(dueDate) < new Date();
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatDateTimeInTZ(date);
};
</script>
